import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where
} from 'firebase/firestore';

import { FinalListFilters } from '../../../types';
import { db } from '../../../firebase';
import { useAuth } from '../../../hooks/useAuth';

interface FinalListVoter {
  id: string;
  voterName: string;
  province: string;
  district: string;
  city_municipality: string;
  barangay: string;
  precinct: string;
}

interface FinalListPrecinctReportProps {
  filters: FinalListFilters;
}

const FinalLisVotersReport: React.FC<FinalListPrecinctReportProps> = ({ filters }) => {
  const { user } = useAuth();
  const [voters, setVoters] = useState<FinalListVoter[]>([]);

  const fetchFinalListVoters = async () => {
    if (!user) return;

    try {
      const finalReleasesRef = collection(db, 'finalReleases');

      let q = query(finalReleasesRef,
        orderBy('voterName'),
        where('province', '==', filters.province),
        where('district', '==', filters.district),
        where('city_municipality', '==', filters.municipality),
        where('barangay', '==', filters.barangay)
      );

      if (filters.precinct) {
        q = query(q, where('precinct', '==', filters.precinct));
      }
      
      const querySnapshot = await getDocs(q);
      
      const votersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as FinalListVoter));

      setVoters(votersList);
    } catch (error) {
      console.error('Error fetching final list voters:', error);
      setVoters([]);
    }
  };

  useEffect(() => {
    fetchFinalListVoters();
  }, [filters, user]);

  const exportToCSV = () => {
    const headers = ['Voter Name', 'Province', 'District', 'Municipality', 'Barangay', 'Precinct'];
    const csvData = voters.map(voter => 
      `"${voter.voterName}","${voter.province || ''}","${voter.district || ''}","${voter.city_municipality || ''}","${voter.barangay || ''}","${voter.precinct || ''}"` 
    );
    
    const csvContent = headers.join(',') + '\n' + csvData.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'final_list_voters.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Final List Voters: {voters.length}
        </Typography>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={exportToCSV}
          disabled={voters.length === 0}
        >
          Export to CSV
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Voter Name</TableCell>
              <TableCell>Precinct</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {voters.map((voter, index) => (
              <TableRow key={voter.id}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{voter.voterName}</TableCell>
                <TableCell>{voter.precinct}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FinalLisVotersReport;
